import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// A pin as an inline SVG divIcon — avoids Leaflet's default marker PNGs, whose URLs break under the
// bundler (the well-known "marker icon not found" issue).
const PIN_ICON = L.divIcon({
  className: "fawkes-map__pin",
  html:
    '<svg width="26" height="26" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7z" ' +
    'fill="#2563eb" stroke="#ffffff" stroke-width="1.5"/>' +
    '<circle cx="12" cy="9" r="2.5" fill="#ffffff"/></svg>',
  iconSize: [26, 26],
  iconAnchor: [13, 26],
});

const NL_CENTER: [number, number] = [52.15, 5.4];
// Jmix DEFAULT_CENTER_ZOOM — the zoom used when the map opens centered on a resolved address (the
// shelter's own location) rather than the whole-country fallback.
const DEFAULT_CENTER_ZOOM = 12;

/**
 * Found-location map (Phase 9), mirroring the Jmix stray-found-location Leaflet map: an OpenStreetMap
 * canvas where clicking drops/moves a pin and reports the lat/long back to the wizard. Bundled Leaflet
 * (offline-capable) rather than the Jmix CDN load. Purely imperative — Leaflet owns the DOM node.
 *
 * {@link defaultCenter} is the initial center used when there is no pin yet — the current shelter's own
 * geocoded address (Jmix resolveCurrentVestigingCoordinates), so the map opens on the shelter's
 * neighbourhood instead of the whole-country fallback. Applied once, and never over a placed pin. It may
 * arrive asynchronously (after the address geocode resolves), so it's handled in an effect, not just at
 * init.
 */
export function FoundLocationMap({
  lat,
  lng,
  onPick,
  defaultCenter,
  interactive = true,
}: {
  lat: number | null;
  lng: number | null;
  /** Omit for a read-only map — without it nothing can move the pin. */
  onPick?: (lat: number, lng: number) => void;
  defaultCenter?: { lat: number; lng: number } | null;
  /**
   * False renders a static preview: no panning, zooming or picking. Used by the stray intake tile, which
   * shows where the animal was found rather than letting you change it (the Jmix tile is a flat image
   * with an enlarge button, not a live map).
   */
  interactive?: boolean;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  // keep the latest onPick without re-running the map-init effect
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;
  // the default center is applied at most once, and only while there's no pin (a pin always wins)
  const appliedDefaultCenterRef = useRef(false);
  /** Whether a pin has ever been placed — the first one always zooms in (see the marker effect). */
  const hadPinRef = useRef(false);

  // create the map once
  useEffect(() => {
    if (!hostRef.current || mapRef.current) return;
    const initialCenter: [number, number] =
      lat != null && lng != null
        ? [lat, lng]
        : defaultCenter
          ? [defaultCenter.lat, defaultCenter.lng]
          : NL_CENTER;
    const initialZoom = lat != null && lng != null ? 15 : defaultCenter ? DEFAULT_CENTER_ZOOM : 7;
    if (defaultCenter) appliedDefaultCenterRef.current = true;
    const map = L.map(hostRef.current, {
      dragging: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
      touchZoom: interactive,
      zoomControl: interactive,
    }).setView(initialCenter, initialZoom);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "&copy; OpenStreetMap contributors",
    }).addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) => onPickRef.current?.(e.latlng.lat, e.latlng.lng));
    mapRef.current = map;
    // the container is often sized after mount (wizard step just rendered) → recompute
    setTimeout(() => map.invalidateSize(), 0);
    return () => {
      map.remove();
      mapRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Center on the shelter's address once it resolves — but only while no pin exists and only once, so it
  // never overrides a placed pin or fights the user panning around.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !defaultCenter || appliedDefaultCenterRef.current) return;
    if (lat != null && lng != null) return;
    appliedDefaultCenterRef.current = true;
    map.setView([defaultCenter.lat, defaultCenter.lng], DEFAULT_CENTER_ZOOM);
  }, [defaultCenter, lat, lng]);

  // reflect the lat/lng into the marker (from a click or an edit-prefill)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (lat == null || lng == null) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      markerRef.current = L.marker([lat, lng], { icon: PIN_ICON }).addTo(map);
    }
    // The FIRST pin always gets a street-level view. The map is created before the coordinates arrive
    // whenever they come from state set in an effect (the stay-edit dialog, an edit-mode prefill), so
    // it starts on the whole-country fallback — and at that zoom the pin is trivially "in view", so the
    // rule below never fired and you were left looking at the Netherlands with a dot on it.
    if (!hadPinRef.current) {
      hadPinRef.current = true;
      map.setView([lat, lng], Math.max(map.getZoom(), 15));
      return;
    }
    // After that, recenter only when the pin lands outside the current view — so a forward-geocode jump
    // (the user typed an address) brings the pin into view, while an in-view manual click doesn't yank
    // the map away from where the operator is looking.
    if (!map.getBounds().contains([lat, lng])) {
      map.setView([lat, lng], Math.max(map.getZoom(), 15));
    }
  }, [lat, lng]);

  return <div ref={hostRef} className="fawkes-inboeken__map" />;
}
